import { useCallback, useState } from 'react';

export interface UseMatchNavigationReturn<T> {
  currentIndex: number;
  currentMatch: T | null;
  matches: T[];
  next: () => void;
  previous: () => void;
  goTo: (index: number) => void;
  markVoted: (index: number) => void;
  isVoted: (index: number) => boolean;
  canGoPrevious: boolean;
  canGoNext: boolean;
  votedCount: number;
  votedIndices: Set<number>;
  isAllVoted: boolean;
}

export function useMatchNavigation<T>(
  matches: T[],
  initialIndex: number = 0,
  totalExpectedMatches?: number
): UseMatchNavigationReturn<T> {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [votedIndices, setVotedIndices] = useState<Set<number>>(new Set());

  const currentMatch = matches[currentIndex] || null;

  const next = useCallback(() => {
    if (currentIndex < matches.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, matches.length]);

  const previous = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const goTo = useCallback(
    (index: number) => {
      // Bound by ACTUAL matches, not expected - can't navigate to non-existent matches
      const maxIndex = Math.min(
        totalExpectedMatches ?? matches.length,
        matches.length
      );
      if (index >= 0 && index < maxIndex) {
        setCurrentIndex(index);
      }
    },
    [matches.length, totalExpectedMatches]
  );

  const markVoted = useCallback((index: number) => {
    setVotedIndices(prev => new Set(prev).add(index));
  }, []);

  const isVoted = useCallback(
    (index: number) => {
      return votedIndices.has(index);
    },
    [votedIndices]
  );

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < matches.length - 1;

  const votedCount = votedIndices.size;
  // Use totalExpectedMatches if provided (it already accounts for run completion)
  const totalMatches = totalExpectedMatches ?? matches.length;
  const isAllVoted = votedCount >= totalMatches && totalMatches > 0;

  return {
    currentIndex,
    currentMatch,
    matches,
    next,
    previous,
    goTo,
    markVoted,
    isVoted,
    canGoPrevious,
    canGoNext,
    votedCount,
    votedIndices,
    isAllVoted,
  };
}
